<template>
	<div
		class="steps__step"
		:class="( is_completed_step ) ? ' steps__step--completed' : ( is_current_step ) ? ' steps__step--active' : null"
	>
		<div class="steps__step-number">
			{{ step.step_number }}
		</div>
		<div class="steps__step-title-wrapper">
			<h2 class="steps__step-title">
				{{ step.title }}
			</h2>
			<template v-if="is_completed_step">
				<h3 class="mt-8 mb-2">
					Design team &amp; developer
				</h3>
				<p
					v-if="no_designer_developer_msg === true"
					class="steps__step-summary"
				>
					No design team or developer selected
				</p>
				<ul
					v-else
					class="steps__step-summary"
				>
					<li v-if="selected_architects.length">
						Architect:
						<span>
							<template
								v-for="item, index in selected_architects"
								:key="'key-client-contacts-' + index"
							>
								{{ item.label + ( index < selected_architects.length - 1 ? ', ' : '' ) }}
							</template>
						</span>
					</li>
					<li v-if="selected_landscape_architects.length">
						Landscape Architect:
						<span>
							<template
								v-for="item, index in selected_landscape_architects"
								:key="'key-client-contacts-' + index"
							>
								{{ item.label + ( index < selected_landscape_architects.length - 1 ? ', ' : '' ) }}
							</template>
						</span>
					</li>
					<li v-if="selected_interior_designers.length">
						Interior Designer:
						<span>
							<template
								v-for="item, index in selected_interior_designers"
								:key="'key-client-contacts-' + index"
							>
								{{ item.label + ( index < selected_interior_designers.length - 1 ? ', ' : '' ) }}
							</template>
						</span>
					</li>
					<li v-if="selected_developers.length">
						Developer:
						<span>
							<template
								v-for="item, index in selected_developers"
								:key="'key-client-contacts-' + index"
							>
								{{ item.label + ( index < selected_developers.length - 1 ? ', ' : '' ) }}
							</template>
						</span>
					</li>
				</ul>
				<h3 class="mt-8 mb-2">
					Partners
				</h3>
				<p
					v-if="no_partner_msg === true"
					class="steps__step-summary"
				>
					No partners selected
				</p>
				<ul
					v-else
					class="steps__step-summary"
				>
					<li v-if="selected_creative_agencies.length">
						Creative Agency:
						<span>
							<template
								v-for="item, index in selected_creative_agencies"
								:key="'key-client-contacts-' + index"
							>
								{{ item.label + ( index < selected_creative_agencies.length - 1 ? ', ' : '' ) }}
							</template>
						</span>
					</li>
					<li v-if="selected_brand_partners.length">
						Brand Partner:
						<span>
							<template
								v-for="item, index in selected_brand_partners"
								:key="'key-client-contacts-' + index"
							>
								{{ item.label + ( index < selected_brand_partners.length - 1 ? ', ' : '' ) }}
							</template>
						</span>
					</li>
					<li v-if="selected_sales_marketing_partners.length">
						Sales &amp; Marketing Partner:
						<span>
							<template
								v-for="item, index in selected_sales_marketing_partners"
								:key="'key-client-contacts-' + index"
							>
								{{ item.label + ( index < selected_sales_marketing_partners.length - 1 ? ', ' : '' ) }}
							</template>
						</span>
					</li>
					<li v-if="selected_hospitality_operators.length">
						Hospitality Operator:
						<span>
							<template
								v-for="item, index in selected_hospitality_operators"
								:key="'key-client-contacts-' + index"
							>
								{{ item.label + ( index < selected_hospitality_operators.length - 1 ? ', ' : '' ) }}
							</template>
						</span>
					</li>
				</ul>
			</template>
		</div>
		<button
			v-if="is_completed_step"
			class="button button--smaller"
			type="button"
			@click="setActiveStep( step.step_number )"
		>
			Edit
		</button>
		<template v-else-if="is_current_step">
			<ExternalTeamFormComponent
				:step-title="step.title"
			/>
		</template>
	</div>
</template>

<script setup>
import { useNewProjectStore } from '../../stores/new-project.js';
import { computed, defineProps, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import ExternalTeamFormComponent from './ExternalTeamFormComponent';

const new_project_store = useNewProjectStore();

const {
	setActiveStep
} = new_project_store;

const {
	selected_architects,
	selected_landscape_architects,
	selected_interior_designers,
	selected_developers,
	selected_creative_agencies,
	selected_brand_partners,
	selected_sales_marketing_partners,
	selected_hospitality_operators
} = storeToRefs( new_project_store );

const props = defineProps( {
	step: {
		required: true,
		type: Object
	},
	activeStepNumber: {
		required: true,
		type: Number
	}
} );

const no_designer_developer_msg = ref( true );
const no_partner_msg = ref( true );

// Show no_designer_developer_msg?

watch( selected_architects, ( updated ) => {
	if ( updated.length > 0 ) {
		no_designer_developer_msg.value = false;
	}
} );

watch( selected_landscape_architects, ( updated ) => {
	if ( updated.length > 0 ) {
		no_designer_developer_msg.value = false;
	}
} );

watch( selected_interior_designers, ( updated ) => {
	if ( updated.length > 0 ) {
		no_designer_developer_msg.value = false;
	}
} );

watch( selected_developers, ( updated ) => {
	if ( updated.length > 0 ) {
		no_designer_developer_msg.value = false;
	}
} );

// Show no_partner_msg?

watch( selected_creative_agencies, ( updated ) => {
	if ( updated.length > 0 ) {
		no_partner_msg.value = false;
	}
} );

watch( selected_brand_partners, ( updated ) => {
	if ( updated.length > 0 ) {
		no_partner_msg.value = false;
	}
} );

watch( selected_sales_marketing_partners, ( updated ) => {
	if ( updated.length > 0 ) {
		no_partner_msg.value = false;
	}
} );

watch( selected_hospitality_operators, ( updated ) => {
	if ( updated.length > 0 ) {
		no_partner_msg.value = false;
	}
} );

const is_current_step = computed( () => {
	return props.step.step_number === props.activeStepNumber;
} );

const is_completed_step = computed( () => {
	return props.step.step_number < props.activeStepNumber;
} );
</script>
